/**
 * Split an assistant message into prose and action markers.
 *
 * The wire contract (coord/SKYAGENT-INC3.md §4) says an action reaches the UI as
 * `[SKYAGENT_ACTION:<uuid>]` inside a `text` chunk, and "the client strips it and
 * renders AgentActionCard". This is the strip.
 *
 * ## Why it is a module with a test rather than a regex inline in the component
 *
 * The failure mode if it is even slightly wrong is that a raw
 * `[SKYAGENT_ACTION:0f3c…]` renders as literal text in front of a counselor — an
 * id shown to a user, which non-negotiable #8 forbids outright — or, worse, that
 * the marker is dropped along with the sentence around it and the human never sees
 * that an action was proposed at all. Both are silent, and neither is visible in a
 * type. So it is pure, exported, and pinned.
 *
 * The UUID is matched STRICTLY. A loose `[^\]]+` would let a model that decided to
 * write the literal string `[SKYAGENT_ACTION:whatever]` into its prose mint a card
 * slot — the model authors this text, so its shape is not trustworthy input.
 */
const MARKER =
  /\[SKYAGENT_ACTION:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]/gi;

export type MessagePart = { kind: "text"; text: string } | { kind: "action"; actionId: string };

/**
 * Parts in source order, with the markers removed from the prose.
 *
 * Empty/whitespace-only text runs are dropped: the model usually puts the marker
 * on its own line, and keeping the blank run either side would render two empty
 * markdown blocks hugging every card.
 */
export function splitActionMarkers(content: string): MessagePart[] {
  const parts: MessagePart[] = [];
  let cursor = 0;

  // A fresh RegExp per call — a module-level /g regex carries `lastIndex` between
  // calls, so sharing one makes the SECOND message parse from wherever the first
  // stopped. That bug renders correctly in every single-message test.
  const re = new RegExp(MARKER.source, "gi");

  let match: RegExpExecArray | null;
  while ((match = re.exec(content)) !== null) {
    const before = content.slice(cursor, match.index);
    if (before.trim()) parts.push({ kind: "text", text: before.trim() });
    parts.push({ kind: "action", actionId: match[1].toLowerCase() });
    cursor = match.index + match[0].length;
  }

  const rest = content.slice(cursor);
  if (rest.trim()) parts.push({ kind: "text", text: rest.trim() });

  return parts;
}

/** True when the message carries at least one action marker. */
export function hasActionMarker(content: string): boolean {
  return new RegExp(MARKER.source, "i").test(content);
}
